import { Injectable, inject } from '@angular/core';
import { LoggerService } from '../logger/logger.service';
import { LocalStorageManagerService } from '../storage/local-storage-manager.service';
import { decryptJioCloudData } from '../config/crypto';

const DEFAULT_JIO_CLOUD_APP_ID = '0123456789abcdef0123456789abcdef';

/**
 * In-memory cache of decrypted API keys — direct port of Consumer's
 * network/ApiKeyCache.ts. Kept as an injectable service (rather than a
 * module-level singleton map) so `clearOnLogout()` can be wired the same way
 * every other logout side effect is: through DI, testable in isolation.
 */
@Injectable({ providedIn: 'root' })
export class ApiKeyCacheService {
  private logger = inject(LoggerService);
  private localStorageManager = inject(LocalStorageManagerService);
  private cache = new Map<string, string>();

  /** Clears cached decrypted keys — call on logout so no stale keys remain in memory. */
  clear(): void {
    this.cache.clear();
  }

  /** Decrypts an encrypted key combo; uses the in-memory cache on subsequent calls. */
  async getDecryptedKey(encryptedCombo: string): Promise<string> {
    const cached = this.cache.get(encryptedCombo);
    if (cached) return cached;

    const appId = this.localStorageManager.getAppId(DEFAULT_JIO_CLOUD_APP_ID);
    const decrypted = await decryptJioCloudData(encryptedCombo, this.logger, appId);
    this.cache.set(encryptedCombo, decrypted);
    return decrypted;
  }
}
